// Gene, Nucleotide, Amino Acid...
import {
  CHAR_WIDTH_IN_PIXEL,
  EXPRESSION_LANE_BAR_HEIGHT,
  EXPRESSION_LANE_GAP,
  STRAND_TYPE,
  TRACK_TYPE,
} from "@/settings/settings.js";

import { type Feature, type Group, type Project } from "../../graphql.js";
import {
  type CallTooltip,
  type IConditionNameLookup,
} from "../../ui-modules/genome-viewer/features-track/FeatureTrack.js";
import { getColorScheme, selectedColorScheme } from "../genome-browser-color.js";
import { createExpressionOverlayShapes } from "./shape-expression.js";
import { type IExpressionParams, type IPileupFeature, type IPileupParams } from "./track-pileup-factory.js";
import {
  ArrowDirection,
  type GenomeViewerShape,
  type GenomeViewerShapeInteractivity,
  type getShapeRenderer,
  type IRenderShapeParams,
} from "./track-shapes-konva-renderers.js";

export interface ICreateDefaultShapeParams {
  feature: IPileupFeature;
  pileupParams: IPileupParams;
  laneNumber: number;
  selected: boolean;
  expressionColoringConditionNameLookup: IConditionNameLookup;
  renderArrow: ReturnType<typeof getShapeRenderer>["renderArrow"];
  callTooltip: CallTooltip;
  groups: Map<Project, Group[]>;
  featureColor?: string; // in case it is defined
}

export interface IFeatureTooltipData {
  pileupFeature: IPileupFeature;
  groups: any;
  expressionColoringConditionNameLookup: any;
}

export interface IExpressionOverlayTooltipData {
  expression: IExpressionParams;
  expressionColoringConditionNameLookup: any;
}

export enum TRACK_TOOLTIP_TYPE {
  FEATURE = "FEATURE",
  NGS = "NGS",
  EXPRESSION_OVERLAY = "EXPRESSION_OVERLAY",
  VARIANT_PROFILE = "VARIANT_PROFILE",
  VARIANT = "VARIANT",
  BLAST_HIT = "BLAST_HIT",
}

const createFeatureShape = (params: ICreateDefaultShapeParams): GenomeViewerShapeInteractivity => {
  const {
    feature,
    pileupParams,
    laneNumber,
    selected,
    renderArrow,
    callTooltip,
    expressionColoringConditionNameLookup,
    groups,
    featureColor,
  } = params;

  // calculate start vertical position of shape
  const shapeY =
    feature.lane *
    (pileupParams.featureHeight +
      pileupParams.lanePadding +
      laneNumber * (EXPRESSION_LANE_GAP + EXPRESSION_LANE_BAR_HEIGHT));

  const direction = getArrowDirectionFromStrand(feature.feature.region.strand);

  const stylingParams: Partial<IRenderShapeParams> = getFeatureShapeStylingProps(
    feature.feature,
    selected,
    featureColor,
  );
  const shape: GenomeViewerShape = renderArrow({
    start: feature.feature.region?.range.start, // feature.x
    stop: feature.feature.region?.range.stop, // feature.x + feature.width
    direction,
    y: shapeY,
    height: pileupParams.featureHeight,
    fill: stylingParams.fill,
    hasStroke: stylingParams.hasStroke,
    inclusiveStartPosition: true,
  });

  if (!pileupParams.hideLabels) {
    shape.text(feature.feature.name, feature.width < feature.feature.name?.length * CHAR_WIDTH_IN_PIXEL, 11);
  }

  if (groups) {
    shape.highlight(...[...groups.keys()].map((project) => project.color));
  }

  const shapeInteractivity: GenomeViewerShapeInteractivity = shape.interactive();

  // TODO this is called at every render ! not optimum
  callTooltip(shapeInteractivity.interactiveShape, shapeInteractivity.tooltipTargetShape, {
    uid: `${feature.feature.type}-${feature.feature.id}`,
    title: feature.feature.name?.length ? feature.feature.name : `id:${feature.feature.id}`,
    type: TRACK_TOOLTIP_TYPE.FEATURE,
    content: {
      pileupFeature: feature,
      groups,
      expressionColoringConditionNameLookup,
    } as IFeatureTooltipData,
  });

  if (feature.expressionParams?.length) {
    createExpressionOverlayShapes({ ...params, referenceShapeYPosition: shapeY });
  }

  return shapeInteractivity;
};

const getFeatureShapeStylingProps = (
  feature: Feature,
  selected: boolean,
  featureColor,
): Partial<IRenderShapeParams> => {
  const colorScheme = getColorScheme(feature.type).colorScheme;
  const direction = getArrowDirectionFromStrand(feature.region.strand);
  const stylingParams: Partial<IRenderShapeParams> = {
    fill: featureColor || colorScheme.light,
    hasStroke: direction === ArrowDirection.Left,
  };

  if (feature.type === TRACK_TYPE.AAFEATURE) {
    if (feature.referenced) {
      stylingParams.fill = getPfamOrInterProColor(feature.name);
      stylingParams.hasStroke = false;
    }
  }

  if (selected) {
    stylingParams.fill = selectedColorScheme.medium;
  }

  return stylingParams;
};

const getPfamOrInterProColor = (name: string): string => {
  const digits = Number(name.match(/^\D*(\d+).*$/)[1]);

  const red: number = Math.abs((digits % 1181) % 256);
  const green: number = Math.abs((digits * 67) % 256);
  const blue: number = Math.abs(((digits * 41) % 883) % 256);

  return `rgb(${red},${green},${blue})`;
};

const getArrowDirectionFromStrand = (strand: string): ArrowDirection => {
  let direction = ArrowDirection.None;
  switch (strand) {
    case STRAND_TYPE.PLUS:
      direction = ArrowDirection.Right;
      break;
    case STRAND_TYPE.MINUS:
      direction = ArrowDirection.Left;
      break;
  }
  return direction;
};

export { createFeatureShape };
